import { Color3, MeshBuilder, StandardMaterial, Vector3 } from "@babylonjs/core";
import { Btn, HeroId, MoveKind, Slot, fxk, hv, pk } from "@rivalwatch/shared";
import * as loops from "../../audio/cues/loops.ts";
import { bell, click, noise, thump, tone, trim, whoosh } from "../../audio/synth.ts";
import type { Cue } from "../../audio/engine.ts";
import type { ClientHero, ProjectileFrame } from "../types.ts";

const V = { tracerNet: 0, climbing: 1 } as const;
const PK_CLUSTER = pk(HeroId.CiderMan, 0);
const FX_WEB = fxk(HeroId.CiderMan, 0);
const FX_SPIN = fxk(HeroId.CiderMan, 1);

const cue =
  (db: number, f: Cue): Cue =>
  trim(db, (o) => f(o));
const webShot = cue(21, (o) => {
  click(o, 0.16, 5200, 0.003);
  whoosh(o, 0.22, 900, 3600, 0.16);
  tone(o, { f: 880, f1: 1320, glide: 0.08, gain: 0.035, d: 0.12 });
  return 0.24;
});
const knuckles = cue(12, (o) => {
  click(o, 0.13, 1800, 0.004);
  thump(o, 0.24, 210, 92, 0.055);
  noise(o, { color: "pink", type: "bandpass", f: 850, f1: 1500, gain: 0.28, d: 0.14 });
  tone(o, { f: 1174, f1: 880, glide: 0.06, gain: 0.07, d: 0.14, delay: 0.01 });
  return 0.22;
});
const webZip = cue(14, (o) => {
  whoosh(o, 0.5, 450, 2400, 0.28);
  thump(o, 0.2, 150, 90, 0.06);
  return 0.35;
});
const uppercut = cue(14, (o) => {
  thump(o, 0.45, 170, 72, 0.1);
  whoosh(o, 0.3, 700, 2200, 0.16);
  return 0.3;
});
const spin = cue(13, (o) => {
  noise(o, { color: "pink", type: "bandpass", f: 1200, f1: 3000, gain: 0.45, d: 0.55 });
  bell(o, 1760, 0.24, 0.4);
  return 0.8;
});

const theme = {
  bpm: 172,
  octave: 1,
  voice: bell,
  notes: [
    [0, 0.25],
    [4, 0.25],
    [7, 0.25],
    [12, 0.5],
    [7, 0.25],
    [16, 0.75],
  ],
} satisfies ClientHero["audio"]["theme"];

const orb = (scene: ProjectileFrame extends never ? never : Parameters<NonNullable<ClientHero["projectiles"]>[number]>[0], _f: ProjectileFrame) => {
  const root = MeshBuilder.CreateSphere("cider-clump", { diameter: 0.26, segments: 8 }, scene);
  const mat = new StandardMaterial("cider-web-projectile", scene);
  mat.emissiveColor = Color3.FromHexString("#f7f7ff");
  mat.diffuseColor = Color3.FromHexString("#f7f7ff");
  root.material = mat;
  root.isPickable = false;
  return {
    gear: { root },
    update(g: { root: typeof root }, frame: ProjectileFrame) {
      g.root.position.set(frame.pos.x, frame.pos.y, frame.pos.z);
      g.root.scaling.setAll(1 + Math.sin(frame.age * 50) * 0.12);
    },
    dispose() {
      mat.dispose();
      root.dispose();
    },
  };
};

const hero: ClientHero = {
  id: HeroId.CiderMan,
  bulk: 0.92,
  shape: { head: 1.05, torso: 0.92, arms: 1.05, legs: 1.05 },
  gear(g) {
    const { scene, add, addWorld, rig, glow, toon, R } = g;
    const wristL = add(MeshBuilder.CreateBox("cider-wrist-l", { width: 0.18, height: 0.08, depth: 0.16 }, scene), rig.handL, glow("#f7f7ff"));
    wristL.position.set(0, 0.02, -0.05);
    const wristR = add(MeshBuilder.CreateBox("cider-wrist-r", { width: 0.18, height: 0.08, depth: 0.16 }, scene), rig.handR, glow("#f7f7ff"));
    wristR.position.set(0, 0.02, -0.05);
    const lenses = add(MeshBuilder.CreateTorus("cider-lenses", { diameter: R * 0.9, thickness: 0.025, tessellation: 16 }, scene), rig.head, glow("#ffffff"));
    lenses.scaling.x = 1.45;
    lenses.scaling.y = 0.55;
    lenses.position.set(0, 0.02, -R * 0.72);
    const satchel = add(MeshBuilder.CreateBox("cider-satchel", { width: R * 0.8, height: 0.26, depth: 0.12 }, scene), rig.torso, toon("#6b3d19"));
    satchel.position.set(R * 0.75, -0.12, 0.24);
    const marker = addWorld(MeshBuilder.CreateTorus("cider-marker", { diameter: 0.85, thickness: 0.035, tessellation: 32 }, scene), glow("#f7f7ff", 0.8));
    marker.isVisible = false;
    const swingLine = addWorld(MeshBuilder.CreateCylinder("cider-swing-web", { diameter: 0.045, height: 1, tessellation: 6 }, scene), glow("#f7f7ff", 0.72));
    swingLine.isVisible = false;
    return { wristL, wristR, marker, swingLine };
  },
  animate(gear, f) {
    const g = gear as { wristL: { scaling: Vector3 }; wristR: { scaling: Vector3 }; marker: ReturnType<typeof MeshBuilder.CreateTorus>; swingLine: ReturnType<typeof MeshBuilder.CreateCylinder> };
    const target = f.v[V.tracerNet] ? f.lookup(Math.round(f.v[V.tracerNet]!)) : undefined;
    g.marker.isVisible = !!target?.alive;
    if (target) {
      g.marker.position.set(target.center.x, target.center.y + target.height * 0.15, target.center.z);
      g.marker.rotation.y = f.t * 4;
      g.marker.scaling.setAll(0.9 + Math.sin(f.t * 8) * 0.08);
    }
    const pulse = 1 + Math.sin(f.t * 20) * 0.08;
    g.wristL.scaling.setAll(pulse);
    g.wristR.scaling.setAll(pulse);
    g.swingLine.isVisible = f.moveKind === MoveKind.Swing;
    if (g.swingLine.isVisible) {
      const a = new Vector3(f.eye.x, f.eye.y - 0.25, f.eye.z);
      const b = new Vector3(f.eye.x + f.aim.x * 18, f.eye.y + Math.max(5, f.aim.y * 18 + 7), f.eye.z + f.aim.z * 18);
      const len = Vector3.Distance(a, b);
      g.swingLine.position = Vector3.Lerp(a, b, 0.5);
      g.swingLine.scaling.y = len;
      g.swingLine.lookAt(b);
      g.swingLine.rotation.x += Math.PI / 2;
    }
    if (f.moveKind === MoveKind.Swing || f.v[V.climbing]) return "holding-both";
    if (f.action === 4 || f.action === 5) return "interact-right";
  },
  hud(c, eid) {
    return [
      { label: "Tracer", value: hv(c, eid, V.tracerNet) > 0 ? 1 : 0, color: "#f7f7ff" },
      { label: "Wall crawl", value: hv(c, eid, V.climbing) > 0 ? 1 : 0, color: "#e04b3a" },
    ];
  },
  lifting(_c, _eid, buttons, grounded) {
    return !grounded && !!(buttons & Btn.Ability1);
  },
  projectiles: {
    [PK_CLUSTER]: orb,
  },
  fx: {
    [FX_WEB]: {
      tracer(c, from, to) {
        const a = new Vector3(from.x, from.y, from.z);
        const b = new Vector3(to.x, to.y, to.z);
        const line = MeshBuilder.CreateCylinder("cider-web-line", { height: Vector3.Distance(a, b), diameter: 0.035, tessellation: 6 }, c.scene);
        line.material = c.glow("#f7f7ff", 0.65);
        line.position = Vector3.Lerp(a, b, 0.5);
        line.lookAt(b);
        line.rotation.x += Math.PI / 2;
        c.timed(line, 0.13, (t, m) => (m.scaling.x = m.scaling.z = 1 - t));
      },
      impact(c, pos) {
        const splat = MeshBuilder.CreateTorus("web-splat", { diameter: 0.65, thickness: 0.04, tessellation: 18 }, c.scene);
        splat.material = c.glow("#f7f7ff", 0.8);
        splat.position.set(pos.x, pos.y, pos.z);
        c.timed(splat, 0.28, (t, m) => (m.scaling.setAll(1 + t)));
      },
    },
    [FX_SPIN]: {
      explosion(c, pos, radius) {
        const ring = MeshBuilder.CreateTorus("cider-spin-ring", { diameter: radius * 2, thickness: 0.08, tessellation: 48 }, c.scene);
        ring.material = c.glow("#e04b3a", 0.55);
        ring.position.set(pos.x, pos.y, pos.z);
        c.timed(ring, 0.7, (t, m) => {
          m.rotation.y += 0.4;
          m.scaling.setAll(0.25 + t);
        });
      },
    },
  },
  audio: {
    weight: "light",
    fire: (ctx) => ({ key: ctx?.alt ? "fire:cider-man:web" : "fire:cider-man:punch", cue: ctx?.alt ? webShot : knuckles }),
    ability: { [Slot.Ability1]: webZip, [Slot.Ability2]: webZip, [Slot.Ability3]: uppercut, [Slot.Ultimate]: spin },
    theme,
    themeTrim: { heroic: 1.5, hostile: -5.5, tease: 4 },
    loops: ({ v, alive }) => (alive && v[V.climbing] > 0 ? [{ key: "crawl", build: loops.climb, gain: 0.55 }] : []),
    flight: { [PK_CLUSTER]: loops.flight("hook") },
  },
  icons: {
    [Slot.Primary]: ["M6 15c2-5 4-7 7-9", "M8 19c3-2 6-3 10-2", "M5 12l4 4M12 7l4 4"],
    [Slot.Secondary]: ["M4 12c5-5 11-5 16 0", "M4 12c5 5 11 5 16 0", "M12 4v16"],
    [Slot.Ability1]: ["M5 20c6-8 9-13 14-16", "M8 11c4 0 7 3 8 7", "M4 20h5"],
    [Slot.Ability2]: ["M4 12h13", "M13 8l4 4-4 4", "M6 6c3 3 3 9 0 12"],
    [Slot.Ability3]: ["M12 20V5", "M7 10l5-5 5 5", "M5 18c4 2 10 2 14 0"],
    [Slot.Ultimate]: ["M12 4a8 8 0 1 0 8 8", "M12 8a4 4 0 1 0 4 4", "M12 12l6-6"],
    passive: ["M6 20V8", "M6 8l4-4 4 4", "M14 20V8", "M3 14h18"],
  },
};

export default hero;
